import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getConsents, updateConsents, type CustomerConsents } from 'API/consents';
import { getNotificationPreferences, updateNotificationPreferences } from 'API/notifications';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PREFERENCE_TOPICS, type NotificationPreferences, type PreferenceTopic } from 'types/engagement.types';
import { fmtDate } from '../shared/format';
import { QueryState } from '../shared/ui';
import { DEFAULT_MATRIX, isCellLocked, normalizeMatrix } from './notification-logic';

type Matrix = NotificationPreferences['matrix'];
type PrefChannel = 'notification' | 'email';

interface PreferencesMatrixProps {
  matrix: Matrix;
  marketingConsent?: boolean;
  onChange(m: Matrix): void;
}

export const PreferencesMatrix: React.FC<PreferencesMatrixProps> = ({ matrix, marketingConsent, onChange }) => {
  const { t } = useTranslation();
  const set = (topic: PreferenceTopic, channel: PrefChannel, value: boolean) =>
    onChange({ ...matrix, [topic]: { ...matrix[topic], [channel]: value } });
  return (
    <Table size={'small'} sx={{ maxWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell />
          <TableCell align={'center'}>{t('engChannel_notification')}</TableCell>
          <TableCell align={'center'}>{t('engChannel_email')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {PREFERENCE_TOPICS.map((topic) => (
          <TableRow key={topic}>
            <TableCell>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{t(`engPrefTopic_${topic}`)}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t(`engPrefTopicHint_${topic}`)}</Typography>
            </TableCell>
            {(['notification', 'email'] as PrefChannel[]).map((channel) => {
              const locked = isCellLocked(topic, channel, marketingConsent);
              const checked = topic === 'account' ? true : locked ? false : matrix[topic][channel];
              const hint = topic === 'account' ? t('engPrefAccountLocked') : locked ? t('engPrefOffersNeedConsent') : '';
              return (
                <TableCell key={channel} align={'center'}>
                  <Tooltip title={hint}>
                    <span>
                      <Checkbox
                        checked={checked}
                        disabled={locked}
                        onChange={(e) => set(topic, channel, e.target.checked)}
                        inputProps={{ 'aria-label': `${t(`engPrefTopic_${topic}`)} ${t(`engChannel_${channel}`)}` }}
                        data-testid={`pref-${topic}-${channel}`}
                      />
                    </span>
                  </Tooltip>
                  {locked && topic === 'offers' ? (
                    <Typography data-testid={'pref-offers-email-note'} sx={{ fontSize: 11, color: 'text.secondary', maxWidth: 140, mx: 'auto' }}>
                      {t('engPrefOffersNeedConsent')}
                    </Typography>
                  ) : null}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

type ConsentKey = 'marketingEmail' | 'adMeasurement';

/**
 * The two optional consents from the sign-up, revocable here. Each switch saves immediately
 * (a consent is a legal act on its own, not part of the matrix draft).
 */
export const ConsentSwitches: React.FC<{
  consents?: CustomerConsents;
  disabled?: boolean;
  onToggle(key: ConsentKey, value: boolean): void;
}> = ({ consents, disabled, onToggle }) => {
  const { t, i18n } = useTranslation();
  const rows: { key: ConsentKey; label: string; at?: string | null; withdrawnAt?: string | null }[] = [
    { key: 'marketingEmail', label: t('engConsentMarketing'), at: consents?.marketingEmailAt, withdrawnAt: consents?.marketingEmailWithdrawnAt },
    { key: 'adMeasurement', label: t('engConsentAdMeasurement'), at: consents?.adMeasurementAt, withdrawnAt: consents?.adMeasurementWithdrawnAt },
  ];
  return (
    <Stack gap={1}>
      {rows.map((r) => {
        const on = consents?.[r.key] === true;
        const stamp = on && r.at ? t('engConsentSince', { at: fmtDate(r.at, i18n.language) }) : !on && r.withdrawnAt ? t('engConsentWithdrawnAt', { at: fmtDate(r.withdrawnAt, i18n.language) }) : '';
        return (
          <Box key={r.key}>
            <FormControlLabel
              control={<Switch checked={on} disabled={disabled || !consents} onChange={(e) => onToggle(r.key, e.target.checked)} data-testid={`consent-${r.key}`} />}
              label={r.label}
              slotProps={{ typography: { fontSize: 14 } }}
            />
            {stamp ? <Typography sx={{ fontSize: 12, color: 'text.secondary', ml: 6 }}>{stamp}</Typography> : null}
          </Box>
        );
      })}
    </Stack>
  );
};

/** Konto → Benachrichtigungen. Saving is explicit (the unsubscribe link lands here, see contract). */
const NotificationPreferencesSection: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['notifications', 'preferences'], queryFn: getNotificationPreferences, retry: false });
  const [matrix, setMatrix] = useState<Matrix>(DEFAULT_MATRIX);
  const [state, setState] = useState<'idle' | 'saved' | 'error'>('idle');
  const consents = useQuery({ queryKey: ['consents'], queryFn: getConsents, retry: false });
  const consentMutation = useMutation({
    mutationFn: (patch: Partial<Record<ConsentKey, boolean>>) => updateConsents(patch),
    onSuccess: (data) => {
      if (data && typeof data === 'object' && 'marketingEmail' in data) queryClient.setQueryData(['consents'], data);
      else queryClient.invalidateQueries({ queryKey: ['consents'] });
    },
  });
  // Offers by e-mail follow the marketing consent; the consents endpoint wins over the preferences hint.
  const marketingConsent = typeof consents.data?.marketingEmail === 'boolean' ? consents.data.marketingEmail : q.data?.marketingEmailConsent;

  useEffect(() => {
    if (q.data) setMatrix(normalizeMatrix(q.data.matrix));
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => updateNotificationPreferences(normalizeMatrix(matrix)),
    onSuccess: () => {
      setState('saved');
      queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
    },
    onError: () => setState('error'),
  });

  return (
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 0.5 }}>{t('engPrefsTab')}</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>{t('engPrefsIntro')}</Typography>
      <QueryState isLoading={q.isLoading} error={q.error}>
        <PreferencesMatrix
          matrix={matrix}
          marketingConsent={marketingConsent}
          onChange={(m) => {
            setState('idle');
            setMatrix(m);
          }}
        />
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5, maxWidth: 640 }}>{t('engPrefsFootnote')}</Typography>
        <Stack direction={'row'} gap={1.5} alignItems={'center'} sx={{ mt: 2 }}>
          <Button variant={'contained'} sx={{ textTransform: 'none' }} disabled={save.isPending} onClick={() => save.mutate()}>
            {t('engSave')}
          </Button>
          {state === 'saved' ? <Alert severity={'success'}>{t('engPrefsSaved')}</Alert> : null}
          {state === 'error' ? <Alert severity={'error'}>{t('engPrefsSaveFailed')}</Alert> : null}
        </Stack>
      </QueryState>

      <Box sx={{ mt: 4, maxWidth: 640 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>{t('engConsentsTitle')}</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>{t('engConsentsIntro')}</Typography>
        {consents.error ? (
          <Alert severity={'error'}>{t('engConsentsLoadFailed')}</Alert>
        ) : (
          <ConsentSwitches
            consents={consents.data}
            disabled={consentMutation.isPending}
            onToggle={(key, value) => consentMutation.mutate({ [key]: value })}
          />
        )}
        {consentMutation.isError ? (
          <Alert severity={'error'} sx={{ mt: 1 }}>
            {t('engConsentSaveFailed')}
          </Alert>
        ) : null}
      </Box>
    </Box>
  );
};

export default NotificationPreferencesSection;
